/**
 * Pure helpers of the shared soft autocomplete (SoftAutocomplete.vue): order selected items first,
 * group the menu, chip overflow, empty state and menu props. No Vue, no API.
 */

export interface MenuHeader {
    header: string;
}

export interface MenuDivider {
    divider: true;
}

export type MenuEntry<T> = T | MenuHeader | MenuDivider;

export type KeyOf<T> = (item: T) => string;
export type GroupOf<T> = (item: T) => string | null | undefined;

export const SELECTED_HEADER = 'Ausgewählt';
export const DEFAULT_MAX_CHIPS = 3;
export const MENU_CONTENT_CLASS = 'ac-menu';

/** Header or divider entry of a Vuetify select list */
export function isMenuMeta(item: unknown): item is MenuHeader | MenuDivider {
    return !!item && typeof item === 'object'
        && (typeof (item as MenuHeader).header === 'string' || (item as MenuDivider).divider === true);
}

/** Selected value(s) of a single or multiple field as strings (ObjectIds, strings, numbers) */
export function selectedKeys(value: unknown): string[] {
    if (value === null || value === undefined || value === '') {
        return [];
    }
    const values = Array.isArray(value) ? value : [value];
    return values.filter((v) => v !== null && v !== undefined && v !== '').map((v) => String(v));
}

/** Value of an item like Vuetify's getPropertyFromItem for a flat key (falls back to the item itself) */
export function itemKey(item: unknown, valueKey = 'value', textKey = 'text'): string {
    if (item !== null && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        if (record[valueKey] !== undefined) {
            return String(record[valueKey]);
        }
        if (record[textKey] !== undefined) {
            return String(record[textKey]);
        }
    }
    return String(item);
}

export interface BuildMenuOptions<T> {
    /** Keys of the items that are pinned to the top (usually the selection when the menu opened) */
    pinnedKeys: string[];
    keyOf: KeyOf<T>;
    /** Optional group label per item; headers are only added when it really separates something */
    groupOf?: GroupOf<T>;
    /** Optional fixed order of group labels; unknown labels follow in order of appearance */
    groupOrder?: string[];
    selectedHeader?: string;
}

/**
 * Menu entries: pinned (selected) items first, then the rest – grouped with headers when a group function
 * separates at least two groups (or the selection from one group). Lists that already contain headers or
 * dividers are returned unchanged. Order inside each part is kept (e.g. distance ranking of the enrichment).
 */
export function buildMenuItems<T>(items: T[], options: BuildMenuOptions<T>): Array<MenuEntry<T>> {
    if (!items.length || items.some((item) => isMenuMeta(item))) {
        return items;
    }
    const pinnedSet = new Set(options.pinnedKeys);
    const pinned = items.filter((item) => pinnedSet.has(options.keyOf(item)));
    const rest = items.filter((item) => !pinnedSet.has(options.keyOf(item)));

    if (!options.groupOf) {
        return pinned.length ? [...pinned, ...rest] : items;
    }

    const groups = new Map<string, T[]>();
    for (const item of rest) {
        const label = options.groupOf(item) || '';
        const group = groups.get(label) || [];
        group.push(item);
        groups.set(label, group);
    }
    const labels = orderGroupLabels(Array.from(groups.keys()), options.groupOrder);
    const useHeaders = labels.length > 1 || (labels.length === 1 && pinned.length > 0 && !!labels[0]);
    if (!useHeaders) {
        return [...pinned, ...rest];
    }

    const result: Array<MenuEntry<T>> = [];
    if (pinned.length) {
        result.push({header: options.selectedHeader || SELECTED_HEADER}, ...pinned);
    }
    for (const label of labels) {
        result.push({header: label || 'Weitere'}, ...(groups.get(label) || []));
    }
    return result;
}

function orderGroupLabels(labels: string[], order?: string[]): string[] {
    if (!order || !order.length) {
        return labels;
    }
    const rank = (label: string) => {
        const index = order.indexOf(label);
        return index === -1 ? order.length + labels.indexOf(label) : index;
    };
    return [...labels].sort((a, b) => rank(a) - rank(b));
}

export interface ChipSlot {
    /** Render a chip for this selection index */
    chip: boolean;
    /** Render the "+n" counter at this index (n = hidden selections) */
    more: number;
}

/** What to render at a selection index: first `max` chips, then one "+n" counter */
export function chipSlot(index: number, total: number, max = DEFAULT_MAX_CHIPS): ChipSlot {
    const limit = Math.max(1, max);
    if (index < limit) {
        return {chip: true, more: 0};
    }
    if (index === limit) {
        return {chip: false, more: total - limit};
    }
    return {chip: false, more: 0};
}

export type EmptyKind = 'loading' | 'no-match' | 'idle';

export interface EmptyState {
    kind: EmptyKind;
    title: string;
    text?: string;
}

/** Content of the menu when there are no items */
export function emptyState(search: string | null | undefined, loading: boolean, idleText?: string | null): EmptyState {
    const term = (search || '').trim();
    if (loading) {
        return {kind: 'loading', title: term ? `Suche nach „${term}“ …` : 'Lädt …'};
    }
    if (term) {
        return {kind: 'no-match', title: `Keine Treffer für „${term}“`, text: 'Anders schreiben oder weniger Zeichen eingeben'};
    }
    return {kind: 'idle', title: idleText || 'Tippen, um zu suchen'};
}

/** Small hint above suggestions: only while nothing is typed and nothing is selected yet */
export function showSuggestionHint(search: string | null | undefined, hasSelection: boolean, itemCount: number, hint?: string | null): boolean {
    return !!hint && !(search || '').trim() && !hasSelection && itemCount > 0;
}

/**
 * Vuetify menu props (object or "auto, offsetY" string) plus the shared content class of the soft menu.
 * Existing content classes are kept.
 */
export function mergeMenuProps(menuProps: unknown, contentClass = MENU_CONTENT_CLASS): Record<string, unknown> {
    let props: Record<string, unknown> = {};
    if (typeof menuProps === 'string') {
        for (const key of menuProps.split(',').map((s) => s.trim()).filter(Boolean)) {
            props[key] = true;
        }
    } else if (menuProps && typeof menuProps === 'object') {
        props = {...(menuProps as Record<string, unknown>)};
    }
    const existing = typeof props.contentClass === 'string' ? props.contentClass : '';
    props.contentClass = existing.split(/\s+/).includes(contentClass) ? existing : `${existing} ${contentClass}`.trim();
    return props;
}

/** The value if it is a CSS colour (hex, rgb[a], hsl[a]); Vuetify colour names like "red darken-2" give '' */
export function cssColor(value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }
    const color = value.trim();
    return /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color) || /^(rgb|hsl)a?\(/i.test(color) ? color : '';
}

/** Texts of the selections hidden behind "+n" (for the tooltip/title) */
export function hiddenSelectionTitle(texts: string[], max = DEFAULT_MAX_CHIPS): string {
    return texts.slice(Math.max(1, max)).join(', ');
}

/** Lower case without diacritics ("Leihgebühr" → "leihgebuhr") for typing filters */
export function normalizeSearchText(value: unknown): string {
    return String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('de-DE').trim();
}

/**
 * v-autocomplete `filter` over several fields of an item: every typed word must occur in one of them (case- and
 * accent-insensitive), e.g. product name + invoice item name. Empty search = everything.
 */
export function fieldsFilter(fields: string[]): (item: unknown, query: string) => boolean {
    return (item: unknown, query: string) => {
        const words = normalizeSearchText(query).split(/\s+/).filter(Boolean);
        if (!words.length) {
            return true;
        }
        const record = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
        const haystack = fields.map((field) => normalizeSearchText(record[field])).join(' ');
        return words.every((word) => haystack.includes(word));
    };
}
